import type {
  BetslipActions,
  BetslipStore,
  SliceCreator,
} from "@/features/betslip/stores/types";
import { exitConfirmingIfMutated } from "@/features/betslip/stores/slices/submission";

// Session state, not slip content - clearing the slip leaves it alone.
export const initialSessionState: Pick<
  BetslipStore,
  "permissions" | "userEventFilters"
> = {
  // null until the session answers; nothing is judged before then.
  permissions: null,
  userEventFilters: [],
};

type SessionActions = Pick<BetslipActions, "setSession">;

// Order-insensitive: the source doesn't promise one, and a reshuffle isn't a
// change worth a write.
export const sameFilters = (a: readonly string[], b: readonly string[]) => {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return seen.size === new Set(b).size && b.every((filter) => seen.has(filter));
};

export const sessionSlice: SliceCreator<
  typeof initialSessionState & SessionActions
> = (set) => ({
  ...initialSessionState,

  // One write - split in two, logout briefly pairs old permissions with new
  // filters.
  setSession: ({ permissions, userEventFilters }) => {
    set((state) => {
      const filtersMoved = !sameFilters(
        state.userEventFilters,
        userEventFilters,
      );
      if (state.permissions === permissions && !filtersMoved) return state;
      // A session change re-derives what's submittable, so the confirm on
      // screen isn't the bet that would go on now.
      return {
        ...exitConfirmingIfMutated(state),
        permissions,
        userEventFilters: filtersMoved
          ? [...userEventFilters]
          : state.userEventFilters,
      };
    });
  },
});
